import {useForm} from 'antd/lib/form/Form';
import {useEffect, useState} from 'react';
import {shallowEqual} from 'react-redux';

import {selectContextRawData} from 'modules/context/context-selector';
import {TEntity} from 'modules/data';
import {loadReferenceData} from 'modules/data/data-actions';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {selectIsThunkPending} from 'store/slices/loading-state-slice/loading-state-slice';

import {loadAisCommentsRequestRowsTableNd, loadAisCommentsRequestTableNd} from '../api/thunks';
import {selectCommentsRequestRowsTableDataNd, selectCommentsRequestTableDataNd} from '../model/selectors';
import {IAisCommentsRequestResultTableDtoNd} from '../model/types';

/**
 * Хук для управления страницей:
 * Запросы на предоставление комментариев к результатам ФА НДПИ, НДД, Акциза на нефтяное сырье.
 *
 * Предоставляет функциональность для:
 * - Загрузки и отображения таблицы запросов комментариев
 * - Управления модальными окнами (основное и сервиса мониторинга)
 * - Работы с формой редактирования данных запроса
 * - Загрузки справочных данных налоговых кодов
 * - Отслеживания состояния загрузки данных
 *
 * @returns Объект с состоянием и методами для управления страницей:
 * - `isModalOpen` - флаг открытия основного модального окна
 * - `isMonitorServiceModalOpen` - флаг открытия модального окна сервиса мониторинга
 * - `currentRequestId` - текущий идентификатор запроса
 * - `form` - экземпляр формы Ant Design для редактирования данных
 * - `aisCommentsRequestTable` - данные таблицы запросов комментариев
 * - `commentsRequestInfo` - детальная информация по выбранному запросу
 * - `isLoadingRequestNumber` - флаг загрузки списка запросов
 * - `isLoadingTable` - флаг загрузки таблицы с детализацией запроса
 * - `taxCodes` - справочник налоговых кодов в виде объекта {код: название}
 * - `setIsMonitorServiceModalOpen` - функция управления модальным окном сервиса мониторинга
 * - `setCurrentRequestId` - функция установки текущего идентификатора запроса
 * - `fetchTableData` - функция перезагрузки данных таблицы
 * - `setIsModalOpen` - функция управления основным модальным окном
 */
export const useAisAnswerFormationNdPage = () => {
    const dispatch = useAppDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMonitorServiceModalOpen, setIsMonitorServiceModalOpen] = useState(false);
    const [currentRequestId, setCurrentRequestId] = useState(null);
    const [form] = useForm<Partial<IAisCommentsRequestResultTableDtoNd>>();
    const [taxCodes, setTaxCodes] = useState<TEntity>({});
    const {organizationId} = useAppSelector(selectContextRawData) ?? {};

    useEffect(() => {
        dispatch(loadReferenceData('/valueLists/SVC_TAX')).then(res => {
            setTaxCodes((res?.data ?? []).reduce((acc, item) => {
                acc[item.lookupCode as string] = item.meaning;
                return acc;
            }, {}));
        });
    }, []);

    const fetchTableData = async () => {
        if (!currentRequestId) return;
        await dispatch(loadAisCommentsRequestRowsTableNd({
            requestId: currentRequestId,
        }));
    };

    useEffect(() => {
        setCurrentRequestId(null);
        form.resetFields();
        setIsModalOpen(false);
        setIsMonitorServiceModalOpen(false);
        dispatch(loadAisCommentsRequestTableNd());
    }, [organizationId]);

    useEffect(() => {
        fetchTableData();
    }, [currentRequestId, organizationId]);

    const isLoadingRequestNumber = useAppSelector(
        st => selectIsThunkPending(st, loadAisCommentsRequestTableNd?.typePrefix),
    );
    const isLoadingTable = useAppSelector(
        st => selectIsThunkPending(st, loadAisCommentsRequestRowsTableNd?.typePrefix),
    );

    const {aisCommentsRequestTable} = useAppSelector(selectCommentsRequestTableDataNd, shallowEqual);
    const commentsRequestInfo = useAppSelector(selectCommentsRequestRowsTableDataNd);

    useEffect(() => {
        if (!currentRequestId) {
            form.resetFields();
            return;
        }
        if (commentsRequestInfo) {
            form.setFieldsValue({
                ...commentsRequestInfo,
                id: currentRequestId,
            });
        }
    }, [commentsRequestInfo, currentRequestId]);

    return {
        isModalOpen,
        isMonitorServiceModalOpen,
        currentRequestId,
        form,
        aisCommentsRequestTable,
        commentsRequestInfo,
        isLoadingRequestNumber,
        isLoadingTable,
        taxCodes,
        setIsMonitorServiceModalOpen,
        setCurrentRequestId,
        fetchTableData,
        setIsModalOpen,
    };
};
